import * as React from 'react';
import type { JsonObject } from '@/app-package';
import { ListingDoc } from '@bb/domain/dist/schemas';
import { Text } from 'components';

type PropertyDetailsProps = {
  listing: JsonObject<Omit<ListingDoc, 'org' | 'reps'>>;
};

const getCondoDetailsData = (
  listing: JsonObject<Omit<ListingDoc, 'org' | 'reps'>>,
) => [
  { label: 'Type', value: listing.condo?.type },
  { label: 'Style', value: listing.condo?.style },
  { label: 'Level', value: listing.condo?.level },
  { label: 'Unit', value: listing.condo?.unit },
  {
    label: 'Rooms',
    value:
      listing.total_rooms &&
      `${listing.total_rooms} ${
        listing.rooms_plus ? `+ ${listing.rooms_plus}` : ''
      }`,
  },
  {
    label: 'Bedrooms',
    value:
      listing.bedrooms &&
      `${listing.bedrooms} ${
        listing.bedrooms_plus ? `+ ${listing.bedrooms_plus}` : ''
      }`,
  },
  { label: 'Bathrooms', value: listing.bathrooms },
  { label: 'Building Name', value: listing.condo?.building_name },
  { label: 'Pets Allowed', value: listing.condo?.pets_allowed },
  { label: 'Balcony', value: listing.condo?.balcony },
  { label: 'Locker Type', value: listing.condo?.lockerType },
  { label: 'Locker', value: listing.condo?.locker },
  { label: 'Garage Type', value: listing.garage_type },
  { label: 'Garage Spaces', value: listing.condo?.garage_spaces },
  { label: 'Parking Spaces', value: listing.condo?.parking_spaces },
  { label: 'Parking Spot', value: listing.condo?.parking_spot },
  { label: 'Parking Cost / Month', value: listing.parking_cost },
  { label: 'Ensuite Laundry', value: listing.condo?.ensuiteLaundry },
  { label: 'Laundry Access', value: listing.condo?.laundryAccess },
];

const getNonCondoDetails = (
  listing: JsonObject<Omit<ListingDoc, 'org' | 'reps'>>,
) => [
  { label: 'Type', value: listing.exteriorType },
  { label: 'Style', value: listing.style },
  {
    label: 'Lot Size',
    value:
      listing.lot_front &&
      listing.lot_depth &&
      `${listing.lot_front} ft x ${listing.lot_depth}`,
  },
  { label: 'Sqft', value: listing.sqft || listing.apx_sqft },
  { label: 'Fronts On', value: listing.fronts_on },
  {
    label: 'Rooms',
    value:
      listing.total_rooms &&
      `${listing.total_rooms} ${
        listing.rooms_plus ? `+ ${listing.rooms_plus}` : ''
      }`,
  },
  {
    label: 'Bedrooms',
    value:
      listing.bedrooms &&
      `${listing.bedrooms} ${
        listing.bedrooms_plus ? `+ ${listing.bedrooms_plus}` : ''
      }`,
  },
  { label: 'Bathrooms', value: listing.bathrooms },
  { label: 'Kitchens', value: listing.kitchens },
  { label: 'Garage Type', value: listing.garage_type },
  { label: 'Parking Spaces', value: listing.parking_spaces },
  { label: 'Drive', value: listing.drive },
  { label: 'Apx Age', value: listing.age },
  { label: 'Water', value: listing.water },
  { label: 'Utilities', value: listing.utilities },
  { label: 'Heat Source', value: listing.heat_source },
  { label: 'Heat Type', value: listing.heat_type },
];

const PropertyDetails: React.FunctionComponent<PropertyDetailsProps> = ({
  listing,
}) => {
  const propertyDetailsData =
    listing.type === 'condo'
      ? getCondoDetailsData(listing)
      : getNonCondoDetails(listing);

  return (
    <div>
      <Text strong>Property Details:</Text>
      <table className="table" style={{ width: '100%' }}>
        <tbody>
          {propertyDetailsData.map(
            ({ label, value }, index) =>
              value && (
                <tr key={index}>
                  <td className="table-key">{`${label}:`}</td>
                  <td>{value.toString().trim()}</td>
                </tr>
              ),
          )}
        </tbody>
      </table>
    </div>
  );
};

export default PropertyDetails;
